import { queryOptions } from '@tanstack/react-query';
import {
  getAccountKpiTargets,
  getStrategyGoalsState,
  listStrategyDocRuns,
  type AccountKpiTargetsWire,
  type StrategyDocRunList,
  type StrategyDocStatus,
  type StrategyGoalsStateWire,
} from './api';
import { fetchPerformanceReport } from '@/lib/apis';
import type { PerformanceData } from '@/lib/types';

// Every strategy query is scoped to the active org so impersonating a
// different user (or switching orgs) doesn't briefly show cached state from
// the previous org. The org id is threaded explicitly from the calling
// component (#4999) — `null` means "no org selected yet" and disables the
// query; the key falls back to 0 so it stays serialisable.

export const strategyGoalsStateQueryKey = (orgId: number) =>
  ['strategy', orgId, 'goals-state'] as const;

// Pulls the persisted Goals-page state blob for the active org. Org-level
// (no Ads account picker) — the active org is resolved server-side from the
// request header. Returns an empty `state` when no row exists yet; the page
// treats that as "keep the atom defaults" rather than wiping the UI. Long
// staleTime because the user's own toggles flush through a mutation that
// hand-writes the cache.
export const strategyGoalsStateQueryOptions = (orgId: number | null) =>
  queryOptions<StrategyGoalsStateWire>({
    queryKey: strategyGoalsStateQueryKey(orgId ?? 0),
    queryFn: () => getStrategyGoalsState(orgId as number),
    staleTime: 10 * 60 * 1000,
    enabled: orgId !== null,
  });

export const accountKpiTargetsQueryKey = (orgId: number, googleadsaccountId: number) =>
  ['strategy', orgId, 'kpi-targets', googleadsaccountId] as const;

// Pulls the persisted KPI target row for the (active org, Ads account) pair.
// Returns all-zero values when no row exists yet — the page treats that as
// "use defaults" rather than as a real value. Long staleTime because the
// user's own edits flush through a mutation that hand-writes the cache.
export const accountKpiTargetsQueryOptions = (
  orgId: number | null,
  googleadsaccountId: number | null,
) =>
  queryOptions<AccountKpiTargetsWire>({
    queryKey: accountKpiTargetsQueryKey(orgId ?? 0, googleadsaccountId ?? 0),
    queryFn: () => getAccountKpiTargets(orgId as number, googleadsaccountId as number),
    staleTime: 10 * 60 * 1000,
    enabled: orgId !== null && googleadsaccountId !== null && googleadsaccountId > 0,
  });

export const strategyKpiPerformanceQueryKey = (orgId: number, campaignIds: string) =>
  ['strategy', orgId, 'kpi-performance', '90d', campaignIds] as const;

// 90 days of daily performance for the account's campaigns, used by the KPI
// card to show historical averages and tracked conversion value. Mirrors the
// performance chart's own fetch; 5 min staleTime keeps the two in step.
export const strategyKpiPerformanceQueryOptions = (orgId: number | null, campaignIds: string) =>
  queryOptions<PerformanceData[]>({
    queryKey: strategyKpiPerformanceQueryKey(orgId ?? 0, campaignIds),
    queryFn: ({ signal }) => fetchPerformanceReport('daily', '90d', campaignIds, signal),
    staleTime: 5 * 60 * 1000,
    enabled: orgId !== null && campaignIds.length > 0,
  });

// --- Strategy-doc generator queries -----------------------------------------

const TERMINAL_DOC_STATUSES: StrategyDocStatus[] = ['succeeded', 'failed'];

export const strategyDocRunsQueryKey = (orgId: number, businessId: number) =>
  ['strategy', orgId, 'doc-runs', businessId] as const;

// Poll the list while any run is non-terminal so the FE picks up
// queued → running → succeeded transitions without manual refetch. Org id is
// threaded explicitly (#4999); `null` disables the query until an org resolves.
export const strategyDocRunsQueryOptions = (orgId: number | null, businessId: number) =>
  queryOptions<StrategyDocRunList>({
    queryKey: strategyDocRunsQueryKey(orgId ?? 0, businessId),
    queryFn: () => listStrategyDocRuns(orgId as number, businessId),
    staleTime: 5 * 1000,
    refetchInterval: (query) => {
      const runs = query.state.data?.runs ?? [];
      const stillRunning = runs.some((r) => !TERMINAL_DOC_STATUSES.includes(r.status));
      return stillRunning ? 3000 : false;
    },
    enabled: orgId !== null && businessId > 0,
  });
